import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ReauthProvider } from './auth/reauth.js';
import { RequireRole } from './auth/RequireRole.js';
import { useSession } from './auth/session-context.js';
import { SessionProvider } from './auth/session.js';
import { AppShell } from './components/AppShell.js';
import { Alert } from './ui/Alert.js';
import { AccountLinkPage } from './pages/AccountLinkPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { ActionDetailPage } from './pages/ActionDetailPage.js';
import { ActionsPage } from './pages/ActionsPage.js';
import { AuditLogPage } from './pages/AuditLogPage.js';
import { EnrollmentTokensPage } from './pages/EnrollmentTokensPage.js';
import { EventDetailPage } from './pages/EventDetailPage.js';
import { EventsPage } from './pages/EventsPage.js';
import { BlocklistPage } from './pages/BlocklistPage.js';
import { CompliancePage } from './pages/CompliancePage.js';
import { TasksPage } from './pages/TasksPage.js';
import { GroupsPage } from './pages/GroupsPage.js';
import { RulesPage } from './pages/RulesPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { SetupPage } from './pages/SetupPage.js';
import { ServerDetailPage } from './pages/ServerDetailPage.js';
import { ServersPage } from './pages/ServersPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { NotificationsPage } from './pages/NotificationsPage.js';
import { IntegrationsPage } from './pages/IntegrationsPage.js';
import { AiPage } from './pages/AiPage.js';
import { AskPage } from './pages/AskPage.js';
import { SuggestionsPage } from './pages/SuggestionsPage.js';
import { PairingPage } from './pages/PairingPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { AddressPage } from './pages/AddressPage.js';
import { TrafficPage } from './pages/TrafficPage.js';
import { TrafficExplorerPage } from './pages/TrafficExplorerPage.js';
import { UsersPage } from './pages/UsersPage.js';

/**
 * The application shape (knowledge doc section 17).
 *
 * One decision here is worth naming: the router is mounted only for a signed-in
 * person. Signed out there is one screen and no navigation, so no page can render
 * half-authenticated and no route needs to defend itself against a missing session.
 */

export function App(): ReactNode {
  return (
    <SessionProvider>
      <AuthenticatedApp />
    </SessionProvider>
  );
}

function AuthenticatedApp(): ReactNode {
  const { status } = useSession();

  if (status === 'loading') {
    return (
      <main className="centred" aria-busy="true">
        <p className="row muted">
          <span className="spinner" aria-hidden="true" />
          Loading…
        </p>
      </main>
    );
  }

  if (status === 'signed-out') {
    // The links that work without an account: the installer's (P8.1), and the
    // invitations and resets an admin makes (P8.4).
    if (window.location.pathname === '/setup') return <SetupPage />;
    if (window.location.pathname === '/account-link') return <AccountLinkPage />;
    return <LoginPage />;
  }

  return (
    <ReauthProvider>
      <BrowserRouter>
        <AppShell>
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/servers" element={<ServersPage />} />
            <Route path="/servers/:id" element={<ServerDetailPage />} />
            <Route
              path="/enrollment-tokens"
              element={
                <RequireRole minimumRole="admin">
                  <EnrollmentTokensPage />
                </RequireRole>
              }
            />
            <Route
              path="/pairing"
              element={
                <RequireRole minimumRole="admin">
                  <PairingPage />
                </RequireRole>
              }
            />
            <Route path="/actions" element={<ActionsPage />} />
            <Route path="/actions/:id" element={<ActionDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/traffic" element={<TrafficPage />} />
            <Route path="/traffic/:dimension" element={<TrafficExplorerPage />} />
            <Route path="/addresses/:ip" element={<AddressPage />} />
            <Route
              path="/groups"
              element={
                <RequireRole minimumRole="admin">
                  <GroupsPage />
                </RequireRole>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireRole minimumRole="admin">
                  <SettingsPage />
                </RequireRole>
              }
            />
            <Route
              path="/notifications"
              element={
                <RequireRole minimumRole="admin">
                  <NotificationsPage />
                </RequireRole>
              }
            />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/blocklist" element={<BlocklistPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/suggestions" element={<SuggestionsPage />} />
            <Route
              path="/ask"
              element={
                <RequireRole minimumRole="operator">
                  <AskPage />
                </RequireRole>
              }
            />
            <Route
              path="/ai"
              element={
                <RequireRole minimumRole="admin">
                  <AiPage />
                </RequireRole>
              }
            />
            <Route
              path="/integrations"
              element={
                <RequireRole minimumRole="admin">
                  <IntegrationsPage />
                </RequireRole>
              }
            />
            <Route
              path="/audit-log"
              element={
                <RequireRole minimumRole="admin">
                  <AuditLogPage />
                </RequireRole>
              }
            />
            <Route
              path="/users"
              element={
                <RequireRole minimumRole="admin">
                  <UsersPage />
                </RequireRole>
              }
            />
            <Route path="/account" element={<AccountPage />} />
            {/* Already signed in: the setup link has nothing left to do. */}
            <Route path="/setup" element={<Navigate to="/" replace />} />
            {/* Signed in: the link is someone else's, or this person's once signed out. */}
            <Route path="/account-link" element={<SignedInAccountLink />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppShell>
      </BrowserRouter>
    </ReauthProvider>
  );
}

/** An invitation or reset link opened while signed in (P8.4). */
function SignedInAccountLink(): ReactNode {
  const { session } = useSession();
  return (
    <div className="page">
      <Alert tone="info" title="You are already signed in">
        This link sets a password for an account. You are signed in as {session?.user.email}: sign
        out first if the link is yours, or pass it on to the person it was made for.
      </Alert>
    </div>
  );
}
